import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row } from 'reactstrap';
import { get } from 'lodash';
import { TbMathXDivideY2, TbShoppingCart } from 'react-icons/tb';
import { GiReceiveMoney } from 'react-icons/gi';
import { FaMoneyBillTransfer } from 'react-icons/fa6';
import { GoLaw } from 'react-icons/go';
import { MdOutlineCalculate } from 'react-icons/md';
import { Col } from 'react-bootstrap';
import ComponentCard from '../../common/components/cards/ComponentCard';
import DetailedSnapshotCard from './DetailedSnapshotCard';
import { getTodayDate } from '../../common/utils/date/Date.utils';
import { fetchFeesStatistics } from '../DashboardActions';

function FeesOverviewSection() {
  const dispatch = useDispatch();
  const feesStatistics = useSelector((state) => state.dashboard.get('feesStatistics'));
  const purchaseStatistics = useSelector((state) => state.dashboard.get('purchaseStatistics'));

  const today = getTodayDate();

  const [dataInitialized, setDataInitialized] = useState(false);

  const [sellersFeesRangeStart, setSellersFeesRangeStart] = useState(today);
  const [sellersFeesRangeEnd, setSellersFeesRangeEnd] = useState(today);

  const [buyersFeesRangeStart, setBuyersFeesRangeStart] = useState(today);
  const [buyersFeesRangeEnd, setBuyersFeesRangeEnd] = useState(today);

  const [buyersSubscriptionFeesRangeStart, setBuyersSubscriptionFeesRangeStart] =
    useState(today);
  const [buyersSubscriptionFeesRangeEnd, setBuyersSubscriptionFeesRangeEnd] = useState(today);

  const [sellersSubscriptionFeesRangeStart, setSellersSubscriptionFeesRangeStart] =
    useState(today);
  const [sellersSubscriptionFeesRangeEnd, setSellersSubscriptionFeesRangeEnd] =
    useState(today);

  const [totalBidsPlacedRangeStart, setTotalBidsPlacedRangeStart] = useState(today);
  const [totalBidsPlacedRangeEnd, setTotalBidsPlacedRangeEnd] = useState(today);

  const [averageBidsNumberRangeStart, setAverageBidsNumberRangeStart] = useState(today);
  const [averageBidsNumberRangeEnd, setAverageBidsNumberRangeEnd] = useState(today);

  const [averageValueOfSoldVehiclesRangeStart, setAverageValueOfSoldVehiclesRangeStart] =
    useState(today);
  const [averageValueOfSoldVehiclesRangeEnd, setAverageValueOfSoldVehiclesRangeEnd] =
    useState(today);

  useEffect(() => {
    if (!dataInitialized) {
      setDataInitialized(true);
      return;
    }
    dispatch(
      fetchFeesStatistics({
        sellersFeesRangeStart,
        sellersFeesRangeEnd,
        buyersFeesRangeStart,
        buyersFeesRangeEnd,
        buyersSubscriptionFeesRangeStart,
        buyersSubscriptionFeesRangeEnd,
        sellersSubscriptionFeesRangeStart,
        sellersSubscriptionFeesRangeEnd,
        totalBidsPlacedRangeStart,
        totalBidsPlacedRangeEnd,
        averageBidsNumberRangeStart,
        averageBidsNumberRangeEnd,
        averageValueOfSoldVehiclesRangeStart,
        averageValueOfSoldVehiclesRangeEnd,
      }),
    );
  }, [
    sellersFeesRangeStart,
    sellersFeesRangeEnd,
    buyersFeesRangeStart,
    buyersFeesRangeEnd,
    buyersSubscriptionFeesRangeStart,
    buyersSubscriptionFeesRangeEnd,
    sellersSubscriptionFeesRangeStart,
    sellersSubscriptionFeesRangeEnd,
    totalBidsPlacedRangeStart,
    totalBidsPlacedRangeEnd,
    averageBidsNumberRangeStart,
    averageBidsNumberRangeEnd,
    averageValueOfSoldVehiclesRangeStart,
    averageValueOfSoldVehiclesRangeEnd,
  ]);

  return (
    <ComponentCard title="Fees Overview" isCollapsible>
      <Row className="mt-2 dashboard__snapshot-detailed-row">
        <Col>
          <DetailedSnapshotCard
            title="Sellers' Fees (ex VAT)"
            prefix="£"
            counterDecimals={2}
            value={get(feesStatistics, 'sellersFees', 0)}
            variant="purple"
            icon={<GiReceiveMoney />}
            showDatePicker
            datePickerStartValue={sellersFeesRangeStart}
            datePickerEndValue={sellersFeesRangeEnd}
            setDatePickerStartValue={setSellersFeesRangeStart}
            setDatePickerEndValue={setSellersFeesRangeEnd}
            defaultDatePickerStartValue={today}
            defaultDatePickerEndValue={today}
            datePickerStartPlaceholder="Today"
            showDateComparison={false}
          />
          <DetailedSnapshotCard
            title="Total Bids Placed"
            value={get(feesStatistics, 'totalBidsPlaced', 0)}
            variant="warning"
            icon={<GoLaw />}
            showDatePicker
            datePickerStartValue={totalBidsPlacedRangeStart}
            datePickerEndValue={totalBidsPlacedRangeEnd}
            setDatePickerStartValue={setTotalBidsPlacedRangeStart}
            setDatePickerEndValue={setTotalBidsPlacedRangeEnd}
            defaultDatePickerStartValue={today}
            defaultDatePickerEndValue={today}
            datePickerStartPlaceholder="Today"
            showDateComparison={false}
          />
        </Col>
        <Col>
          <DetailedSnapshotCard
            title="Buyers' Fees (ex VAT)"
            prefix="£"
            counterDecimals={2}
            value={get(feesStatistics, 'buyersFees', 0)}
            variant="info"
            icon={<GiReceiveMoney />}
            showDatePicker
            datePickerStartValue={buyersFeesRangeStart}
            datePickerEndValue={buyersFeesRangeEnd}
            setDatePickerStartValue={setBuyersFeesRangeStart}
            setDatePickerEndValue={setBuyersFeesRangeEnd}
            defaultDatePickerStartValue={today}
            defaultDatePickerEndValue={today}
            datePickerStartPlaceholder="Today"
            showDateComparison={false}
          />
          <DetailedSnapshotCard
            title="Avg. Bids per Auction"
            value={get(feesStatistics, 'averageBidsNumberPerAuction', 0)}
            variant="secondary"
            counterDecimals={2}
            icon={<TbMathXDivideY2 />}
            showDatePicker
            datePickerStartValue={averageBidsNumberRangeStart}
            datePickerEndValue={averageBidsNumberRangeEnd}
            setDatePickerStartValue={setAverageBidsNumberRangeStart}
            setDatePickerEndValue={setAverageBidsNumberRangeEnd}
            defaultDatePickerStartValue={today}
            defaultDatePickerEndValue={today}
            datePickerStartPlaceholder="Today"
            showDateComparison={false}
          />
        </Col>
        <Col>
          <DetailedSnapshotCard
            title="Buyers' Subscription Fees (ex VAT)"
            prefix="£"
            counterDecimals={2}
            value={get(feesStatistics, 'buyersSubscriptionFees', 0)}
            variant="success"
            icon={<FaMoneyBillTransfer />}
            showDatePicker
            datePickerStartValue={buyersSubscriptionFeesRangeStart}
            datePickerEndValue={buyersSubscriptionFeesRangeEnd}
            setDatePickerStartValue={setBuyersSubscriptionFeesRangeStart}
            setDatePickerEndValue={setBuyersSubscriptionFeesRangeEnd}
            defaultDatePickerStartValue={today}
            defaultDatePickerEndValue={today}
            datePickerStartPlaceholder="Today"
            showDateComparison={false}
          />
          <DetailedSnapshotCard
            title="Avg. Value of Cars Sold"
            value={get(feesStatistics, 'averageValueOfCarsSold', 0)}
            variant="purple"
            counterDecimals={2}
            icon={<MdOutlineCalculate />}
            showDatePicker
            datePickerStartValue={averageValueOfSoldVehiclesRangeStart}
            datePickerEndValue={averageValueOfSoldVehiclesRangeEnd}
            setDatePickerStartValue={setAverageValueOfSoldVehiclesRangeStart}
            setDatePickerEndValue={setAverageValueOfSoldVehiclesRangeEnd}
            defaultDatePickerStartValue={today}
            defaultDatePickerEndValue={today}
            datePickerStartPlaceholder="Today"
            showDateComparison={false}
          />
        </Col>
        <Col>
          <DetailedSnapshotCard
            title="Sellers' Subscription Fees (ex VAT)"
            prefix="£"
            counterDecimals={2}
            value={get(feesStatistics, 'sellersSubscriptionFees', 0)}
            variant="danger"
            icon={<FaMoneyBillTransfer />}
            showDatePicker
            datePickerStartValue={sellersSubscriptionFeesRangeStart}
            datePickerEndValue={sellersSubscriptionFeesRangeEnd}
            setDatePickerStartValue={setSellersSubscriptionFeesRangeStart}
            setDatePickerEndValue={setSellersSubscriptionFeesRangeEnd}
            defaultDatePickerStartValue={today}
            defaultDatePickerEndValue={today}
            datePickerStartPlaceholder="Today"
            showDateComparison={false}
          />
          <DetailedSnapshotCard
            title="Avg. Frequency of Purchase By Month"
            value={get(purchaseStatistics, 'averagePurchaseFrequencyByMonth', 0)}
            variant="info"
            counterDecimals={2}
            icon={<TbShoppingCart />}
            showDateComparison={false}
            footer={
              <p className="d-flex align-items-center gap-1 mb-0 mt-3 dashboard__detailed-snapshot-card-footer">
                <span className="text-nowrap text-body-color fs-5">
                  {get(purchaseStatistics, 'averagePurchaseFrequencyByWeek', 0)} by week
                </span>
              </p>
            }
          />
        </Col>
      </Row>
    </ComponentCard>
  );
}

export default FeesOverviewSection;
